import React, { useState, useEffect } from 'react';
import Navigation from '../../components/Navigation/Navigation';
import { Footer } from '../../components/Footer/Footer';
import ArticleForm from './ArticleForm';
import PracticeAreaForm from './PracticeAreaForm';
import { API_BASE_URL } from '../../config/api';
import './AdminPanel.css';

const AdminPanel = ({ onLogout, onNavigate, lang, setLang, isDarkMode, toggleTheme, onQuickAppointment }) => {
    const [activeTab, setActiveTab] = useState('articles');

    // Articles State
    const [articles, setArticles] = useState([]);
    const [loadingArticles, setLoadingArticles] = useState(true);
    const [showArticleForm, setShowArticleForm] = useState(false);
    const [editingArticle, setEditingArticle] = useState(null);

    // Services State
    const [services, setServices] = useState([]);
    const [loadingServices, setLoadingServices] = useState(true);
    const [showServiceForm, setShowServiceForm] = useState(false);
    const [editingService, setEditingService] = useState(null);

    const token = localStorage.getItem('adminToken');

    useEffect(() => {
        fetchArticles();
        fetchServices();
    }, []);

    // ========== ARTICLES ==========
    const fetchArticles = async () => {
        setLoadingArticles(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/article`);
            if (response.ok) {
                const data = await response.json();
                setArticles(data);
            }
        } catch (error) {
            console.error('Fetch articles error:', error);
        } finally {
            setLoadingArticles(false);
        }
    };

    const handleDeleteArticle = async (articleId) => {
        if (!window.confirm('Bu duyuruyu silmek istediğinizden emin misiniz?')) return;
        try {
            const response = await fetch(`${API_BASE_URL}/api/article/${articleId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                alert('Makale başarıyla silindi!');
                fetchArticles();
            } else {
                alert('Makale silinirken bir hata oluştu!');
            }
        } catch (error) {
            console.error('Delete article error:', error);
            alert('Bağlantı hatası!');
        }
    };

    // ========== SERVICES ==========
    const fetchServices = async () => {
        setLoadingServices(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/practicearea`);
            if (response.ok) {
                const data = await response.json();
                setServices(data);
            }
        } catch (error) {
            console.error('Fetch services error:', error);
        } finally {
            setLoadingServices(false);
        }
    };

    const handleDeleteService = async (serviceId) => {
        if (!window.confirm('Bu hizmeti silmek istediğinizden emin misiniz?')) return;
        try {
            const response = await fetch(`${API_BASE_URL}/api/practicearea/${serviceId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                alert('Hizmet başarıyla silindi!');
                fetchServices();
            } else {
                alert('Hizmet silinirken bir hata oluştu!');
            }
        } catch (error) {
            console.error('Delete service error:', error);
            alert('Bağlantı hatası!');
        }
    };

    // ========== SHARED ==========
    const handleLogout = () => {
        localStorage.removeItem('adminToken');
        if (onNavigate) {
            onNavigate('home', '/');
        } else {
            window.location.href = '/';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        if (dateString.includes('.')) return dateString;
        try {
            const [year, month, day] = dateString.split('-');
            return `${day}.${month}.${year}`;
        } catch {
            return dateString;
        }
    };

    const getThisMonthArticlesCount = () => {
        const now = new Date();
        return articles.filter(article => {
            if (!article.date) return false;
            try {
                let d;
                if (article.date.includes('-')) d = new Date(article.date);
                else if (article.date.includes('.')) {
                    const [day, month, year] = article.date.split('.');
                    d = new Date(year, month - 1, day);
                } else return false;
                return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
            } catch { return false; }
        }).length;
    };

    /* ===== LOADING STATE ===== */
    const LoadingState = () => (
        <div className="loading-state">
            <i className="fa fa-spinner fa-spin"></i>
            <p>Yükleniyor...</p>
        </div>
    );

    /* ===== EMPTY STATE ===== */
    const EmptyState = ({ text, onAdd }) => (
        <div className="empty-state">
            <i className="fa fa-inbox"></i>
            <p>{text}</p>
            <button onClick={onAdd} className="btn-empty-add">İlk Kaydı Ekle</button>
        </div>
    );

    return (
        <div>
            {/* Navigation — onNavigate prop'u geçiriliyor */}
            <Navigation
                lang={lang}
                setLang={setLang}
                isDarkMode={isDarkMode}
                toggleTheme={toggleTheme}
                onQuickAppointment={onQuickAppointment}
                onNavigate={onNavigate}
            />

            <div className="admin-panel" style={{ paddingTop: '100px', minHeight: 'calc(100vh - 200px)' }}>
                <div className="container">

                    {/* Stats Cards */}
                    <div className="stats-section">
                        <div className="stat-card">
                            <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                                <i className="fa fa-file-text"></i>
                            </div>
                            <div className="stat-info">
                                <div className="stat-number">{articles.length}</div>
                                <div className="stat-label">Toplam Duyuru</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                                <i className="fa fa-briefcase"></i>
                            </div>
                            <div className="stat-info">
                                <div className="stat-number">{services.length}</div>
                                <div className="stat-label">Toplam Hizmet</div>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
                                <i className="fa fa-calendar"></i>
                            </div>
                            <div className="stat-info">
                                <div className="stat-number">{getThisMonthArticlesCount()}</div>
                                <div className="stat-label">Bu Ay Eklenen</div>
                            </div>
                        </div>
                    </div>

                    {/* Tab Navigation */}
                    <div className="admin-tabs">
                        <button
                            className={`admin-tab ${activeTab === 'articles' ? 'active' : ''}`}
                            onClick={() => setActiveTab('articles')}
                        >
                            <i className="fa fa-file-text"></i> Duyurular ({articles.length})
                        </button>
                        <button
                            className={`admin-tab ${activeTab === 'services' ? 'active' : ''}`}
                            onClick={() => setActiveTab('services')}
                        >
                            <i className="fa fa-briefcase"></i> Hizmetler ({services.length})
                        </button>
                    </div>

                    {/* ========== ARTICLES TAB ========== */}
                    {activeTab === 'articles' && (
                        <div className="admin-section">
                            <div className="section-header">
                                <h2>Duyuru Yönetimi</h2>
                                <div className="section-header-actions">
                                    <button onClick={handleLogout} className="btn-logout">
                                        <i className="fa fa-sign-out"></i> Çıkış Yap
                                    </button>
                                    <button onClick={() => { setEditingArticle(null); setShowArticleForm(true); }} className="btn-add-new">
                                        <i className="fa fa-plus"></i> Yeni Duyuru Ekle
                                    </button>
                                </div>
                            </div>

                            {loadingArticles ? <LoadingState /> : articles.length === 0 ? (
                                <EmptyState text="Henüz makale bulunmamaktadır." onAdd={() => { setEditingArticle(null); setShowArticleForm(true); }} />
                            ) : (
                                <div className="admin-table-wrapper">
                                    <table className="admin-table">
                                        <thead>
                                            <tr>
                                                <th>BAŞLIK</th>
                                                <th>YAZAR</th>
                                                <th>TARİH</th>
                                                <th>İŞLEMLER</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {articles.map((article) => (
                                                <tr key={article.id}>
                                                    <td>
                                                        <div className="cell-title">
                                                            <strong>{article.titleTR || 'Başlık Yok'}</strong>
                                                            {article.subtitleTR && <small>{article.subtitleTR}</small>}
                                                        </div>
                                                    </td>
                                                    <td>{article.author || '-'}</td>
                                                    <td>{formatDate(article.date)}</td>
                                                    <td>
                                                        <div className="action-buttons">
                                                            <button onClick={() => { setEditingArticle(article); setShowArticleForm(true); }} className="btn-action btn-edit" title="Düzenle">
                                                                <i className="fa fa-pencil"></i>
                                                            </button>
                                                            <button onClick={() => handleDeleteArticle(article.id)} className="btn-action btn-delete" title="Sil">
                                                                <i className="fa fa-trash"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ========== SERVICES TAB ========== */}
                    {activeTab === 'services' && (
                        <div className="admin-section">
                            <div className="section-header">
                                <h2>Hizmet Yönetimi</h2>
                                <div className="section-header-actions">
                                    <button onClick={handleLogout} className="btn-logout">
                                        <i className="fa fa-sign-out"></i> Çıkış Yap
                                    </button>
                                    <button onClick={() => { setEditingService(null); setShowServiceForm(true); }} className="btn-add-new">
                                        <i className="fa fa-plus"></i> Yeni Hizmet Ekle
                                    </button>
                                </div>
                            </div>

                            {loadingServices ? <LoadingState /> : services.length === 0 ? (
                                <EmptyState text="Henüz hizmet bulunmamaktadır." onAdd={() => { setEditingService(null); setShowServiceForm(true); }} />
                            ) : (
                                <div className="admin-table-wrapper">
                                    <table className="admin-table">
                                        <thead>
                                            <tr>
                                                <th>BAŞLIK</th>
                                                <th>İKON</th>
                                                <th>İŞLEMLER</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {services.map((service) => (
                                                <tr key={service.id}>
                                                    <td>
                                                        <div className="cell-title">
                                                            <strong>{service.titleTR || 'Başlık Yok'}</strong>
                                                            {service.descriptionTR && <small>{service.descriptionTR.substring(0, 80)}...</small>}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {service.icon && (
                                                            <i className={`fa ${service.icon}`} style={{ fontSize: '20px', color: '#1e7a46' }}></i>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="action-buttons">
                                                            <button onClick={() => { setEditingService(service); setShowServiceForm(true); }} className="btn-action btn-edit" title="Düzenle">
                                                                <i className="fa fa-pencil"></i>
                                                            </button>
                                                            <button onClick={() => handleDeleteService(service.id)} className="btn-action btn-delete" title="Sil">
                                                                <i className="fa fa-trash"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Article Modal */}
                {showArticleForm && (
                    <div className="modal-overlay" onClick={() => { setShowArticleForm(false); setEditingArticle(null); fetchArticles(); }}>
                        <div className="modal-content form-modal" onClick={(e) => e.stopPropagation()}>
                            <ArticleForm
                                article={editingArticle}
                                onClose={() => { setShowArticleForm(false); setEditingArticle(null); fetchArticles(); }}
                                onSuccess={() => { setShowArticleForm(false); setEditingArticle(null); fetchArticles(); }}
                            />
                        </div>
                    </div>
                )}

                {/* Service Modal */}
                {showServiceForm && (
                    <div className="modal-overlay" onClick={() => { setShowServiceForm(false); setEditingService(null); fetchServices(); }}>
                        <div className="modal-content form-modal" onClick={(e) => e.stopPropagation()}>
                            <PracticeAreaForm
                                practiceArea={editingService}
                                onClose={() => { setShowServiceForm(false); setEditingService(null); fetchServices(); }}
                                onSuccess={() => { setShowServiceForm(false); setEditingService(null); fetchServices(); }}
                            />
                        </div>
                    </div>
                )}
            </div>

            <Footer lang={lang} isDarkMode={isDarkMode} />
        </div>
    );
};

export default AdminPanel;